import {
	FormList,
	FormShape,
	type GetFormInput,
	type GetFormInputSetter,
	type GetFormOutputVerbose,
} from "@suse-o11y/signal/form";

import { readonly } from "~/tools/readonly";

import type { WidgetFormSync } from "../_sync";
import { FormState as ConnectNullsFormState } from "../connect-nulls/form-state";
import { FormState as LegendFormState } from "../legend/form-state";
import { FormState as MinMaxFormState } from "../min-max/form-state";
import {
	ModeFormState as ThresholdsModeFormState,
	StepFormState as ThresholdsStepFormState,
} from "../thresholds/form-state";
import { FormState as YAxisFormState } from "../y-axis/form-state";

import { DEFAULT as DEFAULT_SETTINGS, type Settings } from "./settings";

type FormState = FormShape<{
	connectNulls: ConnectNullsFormState;
	range: FormShape<{
		min: MinMaxFormState;
		max: MinMaxFormState;
	}>;

	legend: LegendFormState;

	yAxis: YAxisFormState;

	thresholds: FormShape<{
		mode: ThresholdsModeFormState;
		steps: FormList<ThresholdsStepFormState>;
	}>;
}>;

const INITIAL: GetFormInput<FormState> = {
	connectNulls: DEFAULT_SETTINGS.connectNulls,
	range: {
		min: MinMaxFormState.initial,
		max: MinMaxFormState.initial,
	},
	legend: LegendFormState.initial,
	yAxis: YAxisFormState.initial,
	thresholds: {
		mode: DEFAULT_SETTINGS.thresholds.mode,
		steps: [],
	},
};

function fromSettings(settings: undefined | Settings) {
	return {
		connectNulls: settings?.connectNulls,
		range: {
			min: settings?.min?.toString(),
			max: settings?.max?.toString(),
		},
		legend: LegendFormState.fromSettings(settings?.legend),
		yAxis: {
			enabled: settings?.yAxis?.show,
			element: {
				unit: settings?.unit,
				decimals: settings?.decimals?.toString(),
				label: settings?.yAxis?.label,
			},
		},
		thresholds: {
			mode: settings?.thresholds?.mode?.toString(),
			steps: readonly(settings?.thresholds?.steps?.map(ThresholdsStepFormState.fromSettings)),
		},
	} satisfies GetFormInputSetter<FormState>;
}

type Input = ReturnType<typeof fromSettings>;

function init(initial: Input): FormState {
	return FormShape({
		connectNulls: ConnectNullsFormState.init(initial.connectNulls ?? INITIAL.connectNulls),

		range: FormShape({
			min: MinMaxFormState.init(initial.range.min),
			max: MinMaxFormState.init(initial.range.max),
		}),

		legend: LegendFormState.init(initial.legend),

		yAxis: YAxisFormState.init(initial.yAxis),

		thresholds: FormShape({
			mode: ThresholdsModeFormState.init(initial.thresholds.mode ?? INITIAL.thresholds.mode),
			steps: FormList(ThresholdsStepFormState.init, {
				initial: initial.thresholds.steps ?? INITIAL.thresholds.steps,
			}),
		}),
	});
}

function toSettings({ connectNulls, yAxis, range, legend, thresholds }: GetFormOutputVerbose<FormState>): Settings {
	return {
		unit: yAxis.element.unit ?? undefined,
		decimals: yAxis.element.decimals ?? undefined,
		connectNulls: connectNulls ?? undefined,
		min: range.min ?? undefined,
		max: range.max ?? undefined,
		yAxis: {
			show: yAxis.enabled ?? undefined,
			label: yAxis.element.label ?? undefined,
		},
		legend: LegendFormState.toSettings(legend),
		thresholds: {
			mode: thresholds.mode ?? undefined,
			steps: thresholds.steps.map(ThresholdsStepFormState.toSettings),
		},
	};
}

function fromSync({ connectNulls, min, max, unit, decimals, legend, yAxis, thresholds }: WidgetFormSync): Input {
	return {
		connectNulls,

		range: { min, max },

		yAxis: {
			enabled: yAxis?.enabled,
			element: {
				decimals,
				unit,
				label: yAxis?.label,
			},
		},

		legend: LegendFormState.fromSync(legend ?? {}),

		thresholds: {
			mode: thresholds?.mode,
			steps: thresholds?.steps,
		},
	};
}

function toSync({ connectNulls, range, yAxis, legend, thresholds }: Input): WidgetFormSync {
	return {
		connectNulls,
		min: range.min,
		max: range.max,
		unit: yAxis.element.unit,
		decimals: yAxis.element.decimals,
		legend: LegendFormState.toSync(legend),
		yAxis: {
			enabled: yAxis.enabled,
			label: yAxis.element.label,
		},
		thresholds: {
			mode: thresholds.mode,
			steps: thresholds.steps?.map(ThresholdsStepFormState.toSync),
		},
	};
}

const FormState = {
	initial: INITIAL,
	init,
	fromSettings,
	toSettings,
	fromSync,
	toSync,
} as const;

export { FormState };
